import { max, min } from "@clockwise/client-commons/src/constants/time-slot";
import { ArrowForward } from "@clockwise/icons";
import { DateTime } from "luxon";
import React, { useEffect } from "react";
import { ECDatePicker } from "../molecules/ECDatePicker";
import {
  evaluateTimesFromEndTimeChange,
  evaluateTimesFromStartTimeChange,
  isValidTimeInput,
  isValidTimeRange,
} from "../utils/eventCardTime.util";

import { Switch } from "@clockwise/design-system";
import classNames from "classnames";
import { isAllDayBasedOnTimes } from "../../web-app-calendar/hooks/useEditExistingEvent.util";
import { Async } from "../atoms/Async";
import { EventElementsWrap } from "../atoms/EventElementsWrap";
import { useReadEventTimeDetails, useUpdateEventTimeDetails } from "../hooks/EventCardContext";
import { ECTimePicker } from "./ECTimePicker";

const getDisplayTime = (time: DateTime) => {
  return time.toFormat("h:mma");
};

export const Time = ({
  readOnly,
  loading,
  hasDateTimeChanged,
  updateFlexToggle,
  timesHaveChanged = false,
  hideUIWrapper = false,
}: {
  readOnly: boolean;
  loading: boolean;
  hasDateTimeChanged?: boolean;
  updateFlexToggle?: () => void;
  timesHaveChanged?: boolean;
  hideUIWrapper?: boolean;
}) => {
  const { startTime, endTime, isAllDay, timeError } = useReadEventTimeDetails();
  const { setStartTime, setEndTime, setIsAllDay, setTimeError } = useUpdateEventTimeDetails();

  // Determine if new times make the event "all day"
  const isAllDayFromTimes = isAllDayBasedOnTimes(startTime, endTime);

  useEffect(() => {
    setIsAllDay(isAllDayFromTimes);
  }, [isAllDayFromTimes, setIsAllDay, loading]);

  const validateAndCallback = (newStart: DateTime, newEnd: DateTime, allDayToggle?: boolean) => {
    if (allDayToggle) {
      setIsAllDay(!isAllDay);
    }
    if (isValidTimeRange(newStart, newEnd)) {
      setTimeError(null);
      setStartTime(newStart);
      setEndTime(newEnd);
      updateFlexToggle?.();
    } else {
      setTimeError("Start time must be before end time.");
    }
  };

  return hideUIWrapper ? (
    <ECTime
      startTime={startTime}
      endTime={endTime}
      readOnly={readOnly}
      onChange={validateAndCallback}
      loading={loading}
      isAllDay={isAllDay}
      hasDateTimeChanged={hasDateTimeChanged}
      timeError={timeError}
    />
  ) : (
    <EventElementsWrap name="time" showChanges={timesHaveChanged}>
      <ECTime
        startTime={startTime}
        endTime={endTime}
        readOnly={readOnly}
        onChange={validateAndCallback}
        loading={loading}
        isAllDay={isAllDay}
        hasDateTimeChanged={hasDateTimeChanged}
        timeError={timeError}
      />
    </EventElementsWrap>
  );
};

export const ECTime = ({
  readOnly,
  startTime,
  endTime,
  onChange,
  loading,
  hasDateTimeChanged,
  isAllDay,
  timeError,
}: {
  readOnly: boolean;
  startTime: DateTime;
  endTime: DateTime;
  onChange: (start: DateTime, end: DateTime, isAllDayToggle?: boolean) => void;
  loading: boolean;
  hasDateTimeChanged?: boolean;
  isAllDay: boolean;
  timeError: string | null;
}) => {
  const [startError, setStartError] = React.useState<string | null>(null);
  const [endError, setEndError] = React.useState<string | null>(null);

  const isStartAndEndSameDay = startTime.hasSame(endTime, "day");

  const onToggleIsAllDay = () => {
    if (!isAllDay) {
      const newEndDate = startTime.plus({ days: 1 });
      const end = endTime.set({
        year: newEndDate.year,
        month: newEndDate.month,
        day: newEndDate.day,
      });
      onChange(startTime, end, true);
    } else {
      const end = endTime.set({
        year: startTime.year,
        month: startTime.month,
        day: startTime.day,
      });
      onChange(startTime, end, true);
    }
  };

  const onChangeStartDate = (iso: DateTime) => {
    const newStartDateValues = {
      year: iso.year,
      month: iso.month,
      day: iso.day,
    };
    const startDateTime = startTime.set(newStartDateValues);

    if (isAllDay) {
      const newEndDate = startDateTime.plus({ days: 1 });
      const newEndDateValues = {
        year: newEndDate.year,
        month: newEndDate.month,
        day: newEndDate.day,
      };
      const endDateTime = endTime.set(newEndDateValues);
      onChange(startDateTime, endDateTime);
    } else if (isStartAndEndSameDay) {
      const endDateTime = endTime.set(newStartDateValues);
      onChange(startDateTime, endDateTime);
    } else {
      onChange(startDateTime, endTime);
    }
  };

  const onChangeStartTime = (newStartTime: string) => {
    if (!isValidTimeInput(newStartTime)) {
      return setStartError("Invalid start time");
    }

    const { start, end } = evaluateTimesFromStartTimeChange(newStartTime, startTime, endTime);

    setStartError(null);
    onChange(start, end);
  };

  const onChangeEndTime = (newEndTime: string) => {
    if (!isValidTimeInput(newEndTime)) {
      return setEndError("Invalid end time");
    }
    const { start, end } = evaluateTimesFromEndTimeChange(newEndTime, startTime, endTime);

    setEndError(null);
    onChange(start, end);
  };

  return (
    <Async loading={loading} loadingHeight="28px" loadingWidth="250px">
      <div className="cw-flex cw-flex-col cw-flex-1">
        <div className="cw-flex cw-items-center cw-justify-between">
          <ECDatePicker
            date={startTime}
            readOnly={readOnly}
            onChange={onChangeStartDate}
            error={timeError}
          />
          {!readOnly && (
            <div>
              <Switch
                checked={isAllDay}
                onChange={onToggleIsAllDay}
                labelPlacement="start"
                disabled={readOnly}
                label={<div className="cw-body-sm cw-font-medium cw-text-muted">All day</div>}
                size="small"
              />
            </div>
          )}
        </div>
        <div className="cw-flex cw-justify-between cw-items-center">
          {!isAllDay && (
            <div
              className={classNames("cw-flex cw-justify-center cw-gap-2", {
                "cw-items-center": readOnly,
                "cw-items-end": !readOnly,
              })}
            >
              <ECTimePicker
                value={getDisplayTime(startTime)}
                onChange={onChangeStartTime}
                slots={Object.keys(min).map((timeslot) => min[timeslot].toUpperCase())}
                readOnly={readOnly}
                error={timeError || startError}
              />
              <ArrowForward
                className={classNames("cw-w-3.5 cw-h-3.5 cw-text-muted-disabled", {
                  "cw-mb-[6px]": !readOnly,
                })}
                aria-hidden
              />
              <div className="cw-flex cw-items-center">
                <ECTimePicker
                  value={getDisplayTime(endTime)}
                  onChange={onChangeEndTime}
                  slots={Object.keys(max).map((timeslot) => max[timeslot].toUpperCase())}
                  readOnly={readOnly}
                  error={timeError || endError}
                />
                <span className="cw-body-sm cw-text-11">
                  {!isStartAndEndSameDay ? "(next day)" : ""}
                </span>
              </div>
            </div>
          )}
          {hasDateTimeChanged && (
            <span className="cw-body-sm cw-text-12 cw-text-default-disabled">New</span>
          )}
        </div>

        {timeError && (
          <div className="cw-text-destructive cw-text-13 cw-font-normal ">{timeError}</div>
        )}
        {startError && (
          <div className="cw-text-destructive cw-text-13 cw-font-normal">{startError}</div>
        )}
        {endError && (
          <div className="cw-text-destructive cw-text-13 cw-font-normal">{endError}</div>
        )}
      </div>
    </Async>
  );
};
